import { useEffect, useId, useRef, useState } from "react";

import {
  AccountRequestError,
  accountErrorMessage,
  activateProjectApiDiscovery,
  confirmProjectApiDiscovery,
  getProjectApiDiscovery,
  saveProjectApiDiscoveryCredential,
  updateProjectApiDiscovery,
  type ProjectApiDiscoveryFinding,
  type ProjectApiDiscoveryAcknowledgement,
  type ProjectCustomHttpSettings,
} from "../../lib/account-client";

type PendingOperation = "confirm" | "activate" | `credential:${string}`;

export interface ApiDiscoveryReviewApi {
  getProjectApiDiscovery: typeof getProjectApiDiscovery;
  updateProjectApiDiscovery: typeof updateProjectApiDiscovery;
  confirmProjectApiDiscovery: typeof confirmProjectApiDiscovery;
  activateProjectApiDiscovery: typeof activateProjectApiDiscovery;
  saveProjectApiDiscoveryCredential: typeof saveProjectApiDiscoveryCredential;
}

export const apiDiscoveryReviewApi: ApiDiscoveryReviewApi = {
  getProjectApiDiscovery,
  updateProjectApiDiscovery,
  confirmProjectApiDiscovery,
  activateProjectApiDiscovery,
  saveProjectApiDiscoveryCredential,
};

export function ApiDiscoveryReview({
  workspaceId,
  projectId,
  expectedRevisionId,
  expectedAgentOperationId,
  onActivated,
  api = apiDiscoveryReviewApi,
}: {
  workspaceId: string;
  projectId: string;
  expectedRevisionId?: string;
  expectedAgentOperationId?: string;
  onActivated?: () => void;
  api?: ApiDiscoveryReviewApi;
}) {
  const errorId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [settings, setSettings] = useState<ProjectCustomHttpSettings | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [acknowledgements, setAcknowledgements] = useState<Record<string, ProjectApiDiscoveryAcknowledgement[]>>({});
  const [pending, setPending] = useState<PendingOperation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { getProjectApiDiscovery } = api;

  useEffect(() => {
    let active = true;
    setSettings(null);
    void getProjectApiDiscovery(workspaceId, projectId)
      .then((result) => {
        if (!active) return;
        setSettings(result);
        setAcknowledgements(result.discoveryDraft?.acknowledgements ?? {});
      })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load this API review."));
      });
    return () => { active = false; };
  }, [getProjectApiDiscovery, loadAttempt, projectId, workspaceId]);

  useEffect(() => {
    if (settings?.discoveryDraft) headingRef.current?.focus();
  }, [settings?.discoveryDraft?.draftId]);

  const recoverStaleDraft = () => {
    setCredentials({});
    setAcknowledgements({});
    setPending(null);
    setError("This review changed in another tab. Doorway cleared unsaved credentials and approvals, then loaded the latest draft.");
    setAnnouncement("The latest discovery draft is loading.");
    setLoadAttempt((attempt) => attempt + 1);
  };

  const fail = (cause: unknown, fallback: string) => {
    if (cause instanceof AccountRequestError && cause.status === 409) {
      recoverStaleDraft();
      return;
    }
    setPending(null);
    setError(accountErrorMessage(cause, fallback));
  };

  const saveCredential = async (alias: string, generation: number) => {
    const credential = credentials[alias] ?? "";
    if (!credential || pending) return;
    setPending(`credential:${alias}`);
    setError(null);
    try {
      const result = await api.saveProjectApiDiscoveryCredential(workspaceId, projectId, {
        alias,
        credential,
        expectedGeneration: generation,
      });
      setSettings(result);
      setCredentials((current) => ({ ...current, [alias]: "" }));
      setPending(null);
      setAnnouncement(`${alias} credential saved.`);
    } catch (cause) {
      fail(cause, `Doorway could not save the ${alias} credential.`);
    }
  };

  const toggleAcknowledgement = (findingId: string, value: ProjectApiDiscoveryAcknowledgement, checked: boolean) => {
    setAcknowledgements((current) => {
      const next = new Set(current[findingId] ?? []);
      if (next.has(value) === checked) return current;
      if (checked) next.add(value);
      else next.delete(value);
      return { ...current, [findingId]: [...next] };
    });
  };

  const confirmReview = async () => {
    const draft = settings?.discoveryDraft;
    if (!draft || pending || !allAcknowledged(draft.evidence?.findings ?? [], acknowledgements)) return;
    setPending("confirm");
    setError(null);
    try {
      const updated = await api.updateProjectApiDiscovery(workspaceId, projectId, {
        draftId: draft.draftId,
        expectedDraftVersion: draft.draftVersion,
        decisions: draft.decisions,
        acknowledgements,
        ...(draft.transformDigest ? { transformDigest: draft.transformDigest } : {}),
        ...(draft.transformApprovalDigest ? { transformApprovalDigest: draft.transformApprovalDigest } : {}),
      });
      const updatedDraft = updated.discoveryDraft;
      if (!updatedDraft) throw new Error("Doorway did not return the updated discovery draft.");
      const confirmed = await api.confirmProjectApiDiscovery(workspaceId, projectId, {
        draftId: updatedDraft.draftId,
        expectedDraftVersion: updatedDraft.draftVersion,
      });
      setSettings(confirmed);
      setAcknowledgements(confirmed.discoveryDraft?.acknowledgements ?? acknowledgements);
      setPending(null);
      setAnnouncement("Review confirmed. Add any remaining credentials, then activate this revision.");
    } catch (cause) {
      fail(cause, "Doorway could not confirm this review.");
    }
  };

  const activate = async () => {
    const draft = settings?.discoveryDraft;
    if (!draft || draft.status !== "ready" || pending || hasMissingCredentials(settings)) return;
    setPending("activate");
    setError(null);
    try {
      const result = await api.activateProjectApiDiscovery(workspaceId, projectId, {
        draftId: draft.draftId,
        expectedDraftVersion: draft.draftVersion,
        ...(expectedAgentOperationId ? { agentOperationId: expectedAgentOperationId } : {}),
      });
      setSettings(result);
      setPending(null);
      if (result.discoveryDraft?.status !== "active") {
        setError("Doorway did not activate the exact reviewed revision. Reload the review before trying again.");
        return;
      }
      setCredentials({});
      setAnnouncement("Exact reviewed revision activated.");
      onActivated?.();
    } catch (cause) {
      fail(cause, "Doorway could not activate this reviewed revision.");
    }
  };

  if (!settings && !error) {
    return <div className="api-discovery-loading" role="status">Loading the API review...</div>;
  }

  const draft = settings?.discoveryDraft;
  if (!draft) {
    return (
      <div className="api-discovery-empty">
        <h2 ref={headingRef} tabIndex={-1}>API review unavailable</h2>
        <p className="error-message" role="alert" id={errorId}>{error ?? "Doorway did not return a discovery draft for this revision."}</p>
        <button className="secondary-button" type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Reload review</button>
      </div>
    );
  }

  if (expectedRevisionId && draft.revisionId !== expectedRevisionId) {
    return (
      <div className="api-discovery-empty">
        <h2 ref={headingRef} tabIndex={-1}>API review changed</h2>
        <p className="error-message" role="alert">Doorway returned a different discovery draft. Reload the published revision before approving anything.</p>
      </div>
    );
  }

  const findings = draft.evidence?.findings ?? [];
  const blockers = draft.blockers ?? [];
  const confirmable = draft.status === "review_required"
    && blockers.length === 0
    && allAcknowledged(findings, acknowledgements);

  return (
    <div className="api-discovery-review" aria-describedby={error ? errorId : undefined}>
      <div className="api-discovery-heading">
        <div>
          <p className="step-label">API review</p>
          <h2 ref={headingRef} tabIndex={-1}>Review the connections Doorway found</h2>
          <p>Doorway analyzed this exact upload. Check the redacted call evidence, add credentials, and confirm the calls your app may make.</p>
        </div>
        <span className={`api-discovery-state api-discovery-state-${draft.status}`}>{draftStatusLabel(draft.status)}</span>
      </div>

      {error ? <p className="error-message" role="alert" id={errorId}>{error}</p> : null}
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>

      {blockers.length > 0 ? (
        <section className="api-discovery-blockers" aria-labelledby="api-discovery-blockers-heading">
          <h3 id="api-discovery-blockers-heading">Resolve these calls before activation</h3>
          <ul>{blockers.map((blocker) => (
            <li key={blocker.id}>
              <strong>{blocker.code.replaceAll("_", " ").toLowerCase()}</strong>
              <span>{blocker.message}</span>
              {blocker.file ? <code>{blocker.file}</code> : null}
            </li>
          ))}</ul>
        </section>
      ) : null}

      <section className="api-discovery-section" aria-labelledby="discovered-credentials-heading">
        <div className="api-discovery-section-heading">
          <div>
            <p className="step-label">Private values</p>
            <h3 id="discovered-credentials-heading">Add credentials</h3>
          </div>
          <p>Values go directly to Doorway&apos;s credential vault and never enter app files or discovery evidence.</p>
        </div>
        {settings?.connections.length ? (
          <ul className="api-discovery-credential-list">
            {settings.connections.map((connection) => {
              const configured = connection.status === "active";
              const inputId = `discovery-credential-${connection.alias}`;
              return (
                <li key={connection.alias}>
                  <div>
                    <strong>{connection.alias}</strong>
                    <small>{configured ? "Credential ready" : "Credential needed"}</small>
                  </div>
                  {settings.canManage ? (
                    <div className="api-discovery-credential-entry">
                      <label htmlFor={inputId}>Credential for {connection.alias}</label>
                      <input
                        id={inputId}
                        type="password"
                        autoComplete="new-password"
                        value={credentials[connection.alias] ?? ""}
                        disabled={pending !== null}
                        onChange={(event) => setCredentials((current) => ({ ...current, [connection.alias]: event.target.value }))}
                      />
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={pending !== null || !(credentials[connection.alias] ?? "")}
                        onClick={() => void saveCredential(connection.alias, connection.generation)}
                      >
                        {pending === `credential:${connection.alias}` ? "Saving..." : `Save ${connection.alias} credential`}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : <p className="workspace-empty">This draft does not require a credential.</p>}
      </section>

      <section className="api-discovery-section" aria-labelledby="discovered-calls-heading">
        <div className="api-discovery-section-heading">
          <div>
            <p className="step-label">Redacted evidence</p>
            <h3 id="discovered-calls-heading">Calls in this upload</h3>
          </div>
          <p>{findings.length} {findings.length === 1 ? "call" : "calls"} found across {draft.evidence?.stats.files ?? 0} source files.</p>
        </div>
        <ol className="api-discovery-finding-list">
          {findings.map((finding) => (
            <li key={finding.id}>
              <div className="api-discovery-call-line">
                <strong>{requestLabel(finding)}</strong>
                <span>{finding.risk === "mutation" ? "Changes data" : finding.risk === "read" ? "Reads data" : "Risk needs review"}</span>
              </div>
              <p><code>{finding.file}</code> <span>byte {finding.span.start}</span></p>
              <dl>
                <div><dt>Runs in</dt><dd>{finding.locus}</dd></div>
                <div><dt>Detected with</dt><dd>{finding.confidence} confidence</dd></div>
                {finding.credentials.map((credential) => (
                  <div key={`${credential.source}:${credential.placement}`}>
                    <dt>Credential source</dt><dd>{credential.source}</dd>
                  </div>
                ))}
              </dl>
              {finding.risk === "mutation" ? (
                <label className="api-discovery-acknowledgement">
                  <input
                    type="checkbox"
                    checked={(acknowledgements[finding.id] ?? []).includes("mutation")}
                    disabled={draft.status !== "review_required" || pending !== null}
                    onChange={(event) => toggleAcknowledgement(finding.id, "mutation", event.target.checked)}
                  />
                  <span>This call can change data. I want Doorway to allow it.</span>
                </label>
              ) : null}
              {finding.confidence !== "high" ? (
                <label className="api-discovery-acknowledgement">
                  <input
                    type="checkbox"
                    checked={(acknowledgements[finding.id] ?? []).includes("low_confidence")}
                    disabled={draft.status !== "review_required" || pending !== null}
                    onChange={(event) => toggleAcknowledgement(finding.id, "low_confidence", event.target.checked)}
                  />
                  <span>I reviewed the low-confidence fields for this call.</span>
                </label>
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      <section className="api-discovery-activation" aria-label="Confirm and activate discovered APIs">
        <div>
          <strong>{draft.status === "ready" ? "Ready for exact activation" : "Confirm this review"}</strong>
          <span>Revision <code>{draft.revisionId}</code> stays separate from the live app until activation succeeds.</span>
        </div>
        {draft.status === "review_required" ? (
          <button className="primary-button" type="button" disabled={!settings?.canManage || !confirmable || pending !== null} onClick={() => void confirmReview()}>
            {pending === "confirm" ? "Confirming..." : "Confirm review"}
          </button>
        ) : null}
        {draft.status === "ready" ? (
          <button className="primary-button" type="button" disabled={!settings?.canManage || hasMissingCredentials(settings) || pending !== null} onClick={() => void activate()}>
            {pending === "activate" ? "Activating..." : "Activate exact revision"}
          </button>
        ) : null}
      </section>
    </div>
  );
}

function requestLabel(finding: ProjectApiDiscoveryFinding): string {
  const target = `${finding.request.origin ?? "unresolved origin"}${finding.request.path ?? ""}`;
  return `${finding.request.method} ${target}`;
}

function requiredAcknowledgements(finding: ProjectApiDiscoveryFinding): ProjectApiDiscoveryAcknowledgement[] {
  return [
    ...(finding.risk === "mutation" ? ["mutation" as const] : []),
    ...(finding.confidence !== "high" ? ["low_confidence" as const] : []),
  ];
}

function allAcknowledged(
  findings: ProjectApiDiscoveryFinding[],
  acknowledgements: Record<string, ProjectApiDiscoveryAcknowledgement[]>,
): boolean {
  return findings.every((finding) => requiredAcknowledgements(finding).every((value) => (
    acknowledgements[finding.id]?.includes(value)
  )));
}

function hasMissingCredentials(settings: ProjectCustomHttpSettings): boolean {
  return settings.connections.some((connection) => connection.status !== "active");
}

function draftStatusLabel(status: NonNullable<ProjectCustomHttpSettings["discoveryDraft"]>["status"]): string {
  return ({
    review_required: "Review required",
    blocked: "Blocked",
    ready: "Ready",
    active: "Active",
    failed: "Needs attention",
    superseded: "Superseded",
    discarded: "Discarded",
  })[status];
}
